import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { Badge } from '@/components/atoms';
import { PersonaSwitcher } from '@/components/PersonaSwitcher/PersonaSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher/ThemeSwitcher';
import { useAuth } from '@/store/useAuth';
import { useBrand } from '@/store/useBrand';
import { useNavPulse } from '@/store/useNavPulse';

import styles from './Header.module.css';

type SubItem = { to?: string; label: string; soon?: boolean };
type NavItem = { to: string; label: string; pulse?: boolean; submenu?: SubItem[] };

// 7 abas principais (Mapa só no 4º). Horóscopo e Redes Sociais têm submenus.
const MAIN_NAV: NavItem[] = [
  { to: '/inteligencia', label: 'Inteligência' },
  { to: '/producao', label: 'Produção', pulse: true },
  { to: '/artigos', label: 'Artigos' },
  { to: '/mapa', label: 'Mapa' },
  { to: '/ncnews', label: 'Gestão' },
  {
    to: '/horoscopo',
    label: 'Horóscopo',
    submenu: [
      { to: '/horoscopo', label: 'Horóscopo' },
      { label: 'Mercado', soon: true },
      { label: 'Resultados Esportivos', soon: true },
      { label: 'Cotações', soon: true },
    ],
  },
  {
    to: '/twitter',
    label: 'Redes Sociais',
    submenu: [
      { to: '/twitter', label: 'Twitter / X' },
      { label: 'Instagram', soon: true },
      { label: 'Facebook', soon: true },
      { label: 'YouTube', soon: true },
      { label: 'TikTok', soon: true },
    ],
  },
];

// Itens secundários — vão para o menu hambúrguer (canto sup. esquerdo, na linha da logo).
const MENU_ITEMS: { to: string; label: string; end?: boolean; admin?: boolean }[] = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/inteligencia/feeddrive', label: 'FeedDrive' },
  { to: '/engineering', label: 'Engenharia', admin: true },
  { to: '/editorias', label: 'Editorias' },
  { to: '/cidades', label: 'Cidades' },
  { to: '/stories', label: 'Stories' },
  { to: '/workflows', label: 'Workflows' },
  { to: '/config', label: 'Configurações', admin: true },
];

export function Header() {
  const navigate = useNavigate();
  const username = useAuth((s) => s.username);
  const role = useAuth((s) => s.role);
  const logout = useAuth((s) => s.logout);
  const brand = useBrand((s) => s.brand);
  const producaoPulse = useNavPulse((s) => s.producao);
  const isAdmin = role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <header className={styles.root}>
      {/* Hambúrguer (itens secundários) + logo, na mesma linha */}
      <div className={styles.brand}>
        <button
          type="button"
          className={styles.hamburger}
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
        {brand.logo_url ? (
          <img className={styles.logo} src={brand.logo_url} alt={brand.name} />
        ) : (
          <span className={styles.logoMark} aria-hidden="true">
            <span className={styles.line} />
            <span className={styles.line} />
            <span className={styles.line} />
          </span>
        )}
        <span className={styles.brandName}>{brand.name}</span>
        <Badge tone="neutral" size="xs">
          v1.1
        </Badge>

        {menuOpen && (
          <>
            <div className={styles.menuBackdrop} onClick={() => setMenuOpen(false)} />
            <div className={styles.menuPanel} role="menu">
              {MENU_ITEMS.filter((i) => !i.admin || isAdmin).map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.end}
                  className={({ isActive }) =>
                    `${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  {i.label}
                </NavLink>
              ))}
              <div className={styles.menuSep} />
              <div className={styles.menuPersona}>
                <span className={styles.menuPersonaLabel}>Visão</span>
                <PersonaSwitcher />
              </div>
            </div>
          </>
        )}
      </div>

      <nav className={styles.nav} aria-label="Navegação principal">
        {MAIN_NAV.map((item) =>
          item.submenu ? (
            <div key={item.to} className={styles.navItem}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                }
              >
                {item.label}
                <span className={styles.caret} aria-hidden="true">▾</span>
              </NavLink>
              <div className={styles.submenu} role="menu">
                {item.submenu.map((s) =>
                  s.soon ? (
                    <span key={s.label} className={styles.submenuSoon}>
                      {s.label}
                      <span className={styles.soonTag}>em breve</span>
                    </span>
                  ) : (
                    <NavLink
                      key={s.label}
                      to={s.to as string}
                      className={({ isActive }) =>
                        `${styles.submenuItem} ${isActive ? styles.submenuItemActive : ''}`
                      }
                    >
                      {s.label}
                    </NavLink>
                  ),
                )}
              </div>
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''} ${
                  item.pulse && producaoPulse ? styles.pulse : ''
                }`
              }
            >
              {item.label}
            </NavLink>
          ),
        )}
      </nav>

      <div className={styles.actions}>
        <ThemeSwitcher />
        {username && (
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            title={`Terminar sessão (${username})`}
          >
            Sair
          </button>
        )}
      </div>
    </header>
  );
}
